<div class="ts-tour" (mouseenter)="pauseSteps()" (focusin)="pauseSteps()">
  <ol class="ts-how__steps" role="tablist" aria-label="How Talespinner works">
    @for (step of steps; track step.number; let i = $index) {
      <li role="presentation">
        <button type="button"
                class="ts-step"
                role="tab"
                [id]="'ts-step-' + i"
                [attr.aria-selected]="activeStep === i"
                [attr.aria-controls]="'ts-panel-' + i"
                [class.is-active]="activeStep === i"
                [class.is-autoplay]="autoplay"
                (click)="selectStep(i)">
          <span class="ts-step__number">{{ step.number }}</span>
          <span class="ts-step__text">
            <span class="ts-step__title">{{ step.title }}</span>
            <span class="ts-step__body">{{ step.body }}</span>
          </span>
          <span class="ts-step__progress" aria-hidden="true"></span>
        </button>
      </li>
    }
  </ol>

  <div class="ts-how__stage">
    <!-- 01 — Start anywhere -->
    <div class="ts-panel" role="tabpanel" id="ts-panel-0" aria-labelledby="ts-step-0" [class.is-active]="activeStep === 0" [attr.aria-hidden]="activeStep !== 0">
      <div class="ts-mock">
        <div class="ts-mock__bar"><span class="t-meta">New project</span><span class="t-caption">Hollow Reach</span></div>
        <div class="ts-mock__body">
          <p class="ts-mock__label t-meta">Where would you like to begin?</p>
          <div class="ts-choices">
            <div class="ts-choice is-picked">
              <span class="ts-choice__title">Genre &amp; writing style</span>
              <span class="ts-choice__hint">Set the tone before anything else</span>
            </div>
            <div class="ts-choice">
              <span class="ts-choice__title">Plot</span>
              <span class="ts-choice__hint">Lay down the backbone</span>
            </div>
            <div class="ts-choice">
              <span class="ts-choice__title">Characters</span>
              <span class="ts-choice__hint">Start with the people</span>
            </div>
          </div>
          <div class="ts-field">
            <span class="ts-field__label t-meta">Genre</span>
            <span class="ts-field__value">Gothic mystery, with a slow-burning romance</span>
          </div>
          <div class="ts-field">
            <span class="ts-field__label t-meta">Writing instructions</span>
            <span class="ts-field__value">Spare sentences. Let the weather do the talking. Close third person.</span>
          </div>
          <div class="ts-field ts-field--inline">
            <span class="ts-field__label t-meta">Language</span>
            <span class="ts-field__value">English</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 02 — Build your world -->
    <div class="ts-panel" role="tabpanel" id="ts-panel-1" aria-labelledby="ts-step-1" [class.is-active]="activeStep === 1" [attr.aria-hidden]="activeStep !== 1">
      <div class="ts-mock">
        <div class="ts-mock__bar"><span class="t-meta">Characters</span><span class="t-caption">3 of 5</span></div>
        <div class="ts-mock__body">
          <div class="ts-profile">
            <div class="ts-profile__initial" aria-hidden="true">M</div>
            <div>
              <p class="ts-profile__name">Mara Ellery</p>
              <p class="ts-profile__role">The forester's daughter, who keeps the lamp</p>
            </div>
            <span class="ts-tag ts-tag--ink">INFJ</span>
          </div>
          <dl class="ts-profile__grid">
            <div><dt class="t-meta">Personality</dt><dd>Watchful, dry, slow to trust and impossible to fool.</dd></div>
            <div><dt class="t-meta">Goal</dt><dd>To learn who has been lighting fires in the pines.</dd></div>
            <div><dt class="t-meta">Relationships</dt><dd>Owes the new warden a debt she never asked for.</dd></div>
          </dl>
          <div class="ts-research">
            <div class="ts-research__head">
              <span class="t-meta">Research notes</span>
              <span class="ts-toggle is-on"><span class="ts-toggle__knob"></span>Web research</span>
            </div>
            <ul class="ts-research__list">
              <li>How fire lookouts worked in the 1920s</li>
              <li>Folklore of the northern pine forests</li>
              <li>The geography of Hollow Reach</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- 03 — Write together -->
    <div class="ts-panel" role="tabpanel" id="ts-panel-2" aria-labelledby="ts-step-2" [class.is-active]="activeStep === 2" [attr.aria-hidden]="activeStep !== 2">
      <div class="ts-mock ts-mock--page">
        <div class="ts-mock__bar"><span class="t-meta">Chapter 3</span><span class="t-caption">1,640 words</span></div>
        <div class="ts-mock__body ts-manuscript">
          <p class="ts-manuscript__title">The Lamp in the Pines</p>
          <p>The fog came up from the river before dusk, as it always did in October, and by the time Mara reached the lookout the trees had lost their tops.</p>
          <p><mark class="ts-manuscript__selection">She climbed the ladder without counting the rungs.</mark> Counting was for people who expected to come back down. The lamp was already lit.</p>
          <p class="ts-manuscript__fade">Someone had trimmed the wick, and recently.</p>

          <div class="ts-menu">
            <div class="ts-menu__item is-active">Rewrite selection…</div>
            <div class="ts-menu__item">Insert text at cursor…</div>
            <div class="ts-menu__prompt">
              <span class="t-meta">Prompt</span>
              <span class="ts-menu__input">Colder. Let her fear show in one detail.</span>
            </div>
            <div class="ts-menu__model t-caption">with {{ modelName }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 04 — Share it -->
    <div class="ts-panel" role="tabpanel" id="ts-panel-3" aria-labelledby="ts-step-3" [class.is-active]="activeStep === 3" [attr.aria-hidden]="activeStep !== 3">
      <div class="ts-mock">
        <div class="ts-mock__bar"><span class="t-meta">Publish</span><span class="t-caption">12 chapters · finished</span></div>
        <div class="ts-mock__body ts-publish">
          <div class="ts-publish__cover">
            <img src="assets/images/book-cover-placeholder8.webp" alt="" loading="lazy">
            <div class="ts-publish__cover-title">
              <span>Hollow Reach</span>
            </div>
          </div>
          <div class="ts-publish__actions">
            <p class="ts-tag ts-tag--accent">Cover generated</p>
            <div class="ts-publish__row"><span>Download as PDF</span><span class="t-caption">↓</span></div>
            <div class="ts-publish__row"><span>Copy read-only link</span><span class="t-caption">⧉</span></div>
            <div class="ts-publish__row"><span>Public on Discover</span><span class="ts-toggle is-on"><span class="ts-toggle__knob"></span></span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
